import moment from 'moment-timezone';
import { env } from './env';

const TZ = env.TIMEZONE;

// Bahasa Indonesia day/month names. We map these ourselves (rather than
// moment.locale('id')) because Vite's dep pre-bundling registers the locale on
// a separate moment copy, so the global locale silently falls back to English.
// moment stays responsible for timezone + numeric parts (locale-independent).
const ID_DAYS = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
const ID_MONTHS = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];

// Short month names, for the narrow 58mm receipt. Indonesian "Mei" is already
// three letters; "Agu" follows the common Jan/Feb/…/Des pattern.
const ID_MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
const EN_MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface DateFormatOptions {
  /** Abbreviated month ("Nov" instead of "November") — used by the receipt. */
  shortMonth?: boolean;
}

/** Format a number as Indonesian Rupiah, no decimals. */
export function rupiah(value: number | null | undefined): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

/** Format a plain integer with id-ID grouping (no currency symbol). */
export function number(value: number | null | undefined): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('id-ID').format(n);
}

/**
 * The park's civil day (YYYY-MM-DD) at an absolute instant — Asia/Jakarta by
 * configuration, never the terminal's OS zone. `Date#getFullYear()` and
 * friends resolve in the OS zone and `toISOString().slice(0,10)` gives the UTC
 * day; both are wrong for a civil day (see the root CLAUDE.md), so moment-tz
 * does the conversion.
 */
export function civilDayISO(atMs: number = Date.now()): string {
  return moment.tz(atMs, TZ).format('YYYY-MM-DD');
}

/**
 * The absolute instant (epoch ms) of a civil wall-clock time in the park's
 * zone: `civilTimeMs('2026-10-20', 9, 30)` is 09:30 WIB that day, whatever the
 * OS thinks local time is. `NaN` when the pieces don't form a real date, so
 * callers can tell "unknown" from "midnight".
 */
export function civilTimeMs(dayISO: string, hour: number, minute: number): number {
  const parts = String(dayISO).split('-').map(Number);
  const [y, mo, d] = parts;
  if (parts.length !== 3 || y === undefined || mo === undefined || d === undefined) return NaN;
  const m = moment.tz(
    { year: y, month: mo - 1, day: d, hour, minute, second: 0, millisecond: 0 },
    TZ,
  );
  return m.isValid() ? m.valueOf() : NaN;
}

/** Today's date in the configured timezone as YYYY-MM-DD. */
export function todayISO(): string {
  return civilDayISO();
}

/** Add days to today (timezone-aware) and return YYYY-MM-DD. */
export function addDaysISO(days: number): string {
  return moment.tz(TZ).add(days, 'days').format('YYYY-MM-DD');
}

/** Compact numeric date for form fields (e.g. "05-07-2026"). */
export function formatDMY(iso: string | undefined | null): string {
  if (!iso) return '';
  return moment.tz(iso, TZ).format('DD-MM-YYYY');
}

/**
 * Human-readable date in Bahasa Indonesia (e.g. "Minggu, 05 Juli 2026";
 * with `shortMonth`, "Minggu, 05 Jul 2026").
 */
export function formatDate(iso: string | undefined | null, opts: DateFormatOptions = {}): string {
  if (!iso) return '—';
  const m = moment.tz(iso, TZ);
  const months = opts.shortMonth ? ID_MONTHS_SHORT : ID_MONTHS;
  return `${ID_DAYS[m.day()]}, ${m.format('DD')} ${months[m.month()]} ${m.format('YYYY')}`;
}

const EN_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Human-readable date+time in Bahasa Indonesia (e.g. "05 Juli 2026 · 05:43";
 * with `shortMonth`, "05 Jul 2026 · 05:43").
 * #1853: `locale: 'en'` keeps the same layout with English month names.
 */
export function formatDateTime(
  iso: string | undefined | null,
  locale: 'id' | 'en' = 'id',
  opts: DateFormatOptions = {},
): string {
  if (!iso) return '—';
  const m = moment.tz(iso, TZ);
  const months =
    locale === 'en'
      ? opts.shortMonth ? EN_MONTHS_SHORT : EN_MONTHS
      : opts.shortMonth ? ID_MONTHS_SHORT : ID_MONTHS;
  return `${m.format('DD')} ${months[m.month()]} ${m.format('YYYY')} · ${m.format('HH:mm')}`;
}

/** Time-only (HH:mm) for slot windows. */
export function formatTime(iso: string | undefined | null): string {
  if (!iso) return '—';
  return moment.tz(iso, TZ).format('HH:mm');
}

/** Is the given YYYY-MM-DD within today..+maxDays (timezone-aware)? */
export function isWithinRange(dateISO: string, maxDays = 7): boolean {
  const d = moment.tz(dateISO, 'YYYY-MM-DD', TZ).startOf('day');
  const start = moment.tz(TZ).startOf('day');
  const end = moment.tz(TZ).add(maxDays, 'days').endOf('day');
  return d.isSameOrAfter(start) && d.isSameOrBefore(end);
}
